import { useEffect, useRef, useState } from "react";
import NavbarAdmin from "../components/NavbarAdmin";
import { useLanguage } from "../context/LanguageContext";
import Toast from "../components/Toast";
import { t } from "../i18n";
import { buildPromptPayPayload, isValidPromptPayId, refreshPromptPayConfig } from "../utils/promptpay";

const API = process.env.REACT_APP_API_URL || "";
const getToken = () => localStorage.getItem("token");
const authHeader = () => ({ Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" });

const inputStyle = {
  width: "100%", padding: "10px 14px", borderRadius: 8,
  border: "1px solid #d1d5db", fontSize: 14, background: "#fff",
  color: "#111827", boxSizing: "border-box", outline: "none", fontFamily: "inherit",
};
const labelStyle = { fontSize: 13, color: "#374151", display: "block", marginBottom: 6, fontWeight: 400 };
const cardStyle  = { background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb", padding: "20px 24px" };
const btnDark    = { padding: "11px", borderRadius: 8, border: "none", background: "#1e3a5f", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 500 };

function QRPreview({ phone, size = 200, emptyLabel }) {
  const canvasRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!canvasRef.current || !isValidPromptPayId(phone)) { setReady(false); return; }
    const payload = buildPromptPayPayload(phone, 0);
    import("qrcode")
      .then((QRCode) => {
        QRCode.toCanvas(canvasRef.current, payload, {
          errorCorrectionLevel: "M", width: size, margin: 2,
          color: { dark: "#1a1a2e", light: "#ffffff" },
        }, () => setReady(true));
      })
      .catch(() => setReady(false));
  }, [phone, size]);

  const valid = isValidPromptPayId(phone);

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
      <div style={{ width: size, height: size, borderRadius: 12, border: "1px solid #e5e7eb",
                    display: "flex", alignItems: "center", justifyContent: "center", background: "#f9fafb", overflow: "hidden" }}>
        {valid
          ? <canvas ref={canvasRef} style={{ display: "block", opacity: ready ? 1 : 0, transition: "opacity .2s" }} />
          : <span style={{ fontSize: 12, color: "#9ca3af", textAlign: "center", padding: "0 16px" }}>{emptyLabel}</span>}
      </div>
    </div>
  );
}

export default function PaymentSettings() {
  const { lang } = useLanguage();
  const [profileId, setProfileId] = useState(null);
  const [hotelName, setHotelName] = useState("");
  const [phone,       setPhone]       = useState("");
  const [accountName, setAccountName] = useState("");
  const [bankName,    setBankName]    = useState("");
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [toast, setToast] = useState("");
  const showToast = msg => { setToast(msg); setTimeout(() => setToast(""), 2400); };

  useEffect(() => {
    fetch(`${API}/api/profiles?type=hotel`, { headers: authHeader() })
      .then(r => r.json())
      .then(d => {
        if (Array.isArray(d) && d.length > 0) {
          const hotel = d[0];
          setProfileId(hotel._id);
          setHotelName(hotel.name || "");
          setPhone(hotel.promptpayPhone || "");
          setAccountName(hotel.promptpayName || "");
          setBankName(hotel.promptpayBank || "");
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    if (!profileId && !hotelName.trim()) { showToast(t(lang, "psToastHotelRequired")); return; }
    if (phone && !isValidPromptPayId(phone)) { showToast(t(lang, "psToastPhoneInvalid")); return; }
    setSaving(true);
    try {
      const body = {
        promptpayPhone: phone.trim(),
        promptpayName:  accountName.trim(),
        promptpayBank:  bankName.trim(),
      };
      const res = profileId
        ? await fetch(`${API}/api/profiles/${profileId}`, { method: "PUT", headers: authHeader(), body: JSON.stringify(body) })
        : await fetch(`${API}/api/profiles`, { method: "POST", headers: authHeader(), body: JSON.stringify({ ...body, type: "hotel", name: hotelName.trim() }) });
      if (!res.ok) throw new Error();
      const saved = await res.json();
      setProfileId(saved._id);
      refreshPromptPayConfig();
      showToast(t(lang, "psToastSaved"));
    } catch { showToast(t(lang, "psToastError")); }
    setSaving(false);
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f9fafb" }}>
      <NavbarAdmin />
      <Toast show={!!toast} type="info" message={toast} lang={lang} />

      <div style={{ maxWidth: 720, margin: "0 auto", padding: "24px 16px" }}>
        <p style={{ margin: "0 0 2px", fontWeight: 700, fontSize: 18, color: "#111827" }}>{t(lang, "psTitle")}</p>
        <p style={{ margin: "0 0 20px", fontSize: 13, color: "#6b7280" }}>{t(lang, "psSubtitle")}</p>

        {loading ? (
          <div style={{ ...cardStyle, textAlign: "center", color: "#9ca3af", fontSize: 13 }}>{t(lang, "psLoading")}</div>
        ) : (
          <div style={{ ...cardStyle, display: "flex", gap: 24, flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 280px", display: "flex", flexDirection: "column", gap: 14 }}>
              {!profileId && (
                <div>
                  <label style={labelStyle}>{t(lang, "psHotelName")}</label>
                  <input value={hotelName} onChange={e => setHotelName(e.target.value)}
                    placeholder={t(lang, "psHotelNamePh")} style={inputStyle} />
                </div>
              )}
              <div>
                <label style={labelStyle}>{t(lang, "psPhone")}</label>
                <input value={phone} maxLength={18}
                  onChange={e => setPhone(e.target.value.replace(/[^0-9-\s]/g, ""))}
                  placeholder={t(lang, "psPhonePh")} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>{t(lang, "psAccountName")}</label>
                <input value={accountName} onChange={e => setAccountName(e.target.value)}
                  placeholder={t(lang, "psAccountNamePh")} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>{t(lang, "psBankName")}</label>
                <input value={bankName} onChange={e => setBankName(e.target.value)}
                  placeholder={t(lang, "psBankNamePh")} style={inputStyle} />
              </div>
              <button onClick={handleSave} disabled={saving} style={{ ...btnDark, opacity: saving ? 0.7 : 1, marginTop: 4 }}>
                {saving ? t(lang, "psSaving") : t(lang, "psSave")}
              </button>
            </div>

            <div style={{ flex: "0 0 auto", display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
              <label style={labelStyle}>{t(lang, "psPreview")}</label>
              <QRPreview phone={phone} emptyLabel={t(lang, "psPreviewEmpty")} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
